import { Type } from '@angular/core';
import { signalStore, withState, withMethods, withComputed, patchState } from '@ngrx/signals';
import { computed } from '@angular/core';

export interface WizardStep {
  componentType: Type<any>;
  id: string;
  label?: string;
  title?: string;
  mobileTitle?: boolean;
  isActive: boolean;
  isCompleted: boolean;
  lastStep?: boolean;
  noButtons?: boolean;
  hasTour?: boolean;
  subtitle?: string;
  order: number;
  onNext?: () => boolean;
  nextLabel?: string;
  backLabel?: string;
  resetLabel?: string;
  errorMessage?: string;
  fullWidthNext?: boolean;
  hidden?: () => boolean;
  minHeight?: string;
}

export interface WizardState {
  currentStep: string;
  steps: WizardStep[];
}

const initialState: WizardState = {
  currentStep: '',
  steps: [],
};

export const WizardReportStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((state) => ({
    // Computed properties derived from state
    currentStep: computed(() => state.currentStep()),
    activeStep: computed(() => state.steps().find((step) => step.isActive)),
    visibleSteps: computed(() => state.steps().filter((step) => !step.hidden || !step.hidden())),
    isFirstStep: computed(() => state.currentStep() === state.steps()[0]?.id),
    isLastStep: computed(() => {
      const currentStepIndex = state.steps().findIndex((step) => step.isActive);
      return currentStepIndex === state.steps().length - 1;
    }),
    hasTour: computed(() => {
      const activeStep = state.steps().find((step) => step.isActive);
      return !!activeStep?.hasTour;
    }),
    shouldShowFullWidthNext: computed(() => {
      const activeStep = state.steps().find((step) => step.isActive);
      return !!activeStep?.fullWidthNext;
    }),
    noButtons: computed(() => {
      const currentStepIndex = state.steps().findIndex((step) => step.isActive);
      return !!state.steps()[currentStepIndex]?.noButtons;
    }),
    stepTitle: computed(() => {
      const activeStep = state.steps().find((step) => step.isActive);
      return activeStep?.title || '';
    }),
    stepSubtitle: computed(() => {
      const activeStep = state.steps().find((step) => step.isActive);
      return activeStep?.subtitle;
    }),
    nextButtonLabel: computed(() => {
      const activeStep = state.steps().find((step) => step.isActive);
      return activeStep?.nextLabel || 'next';
    }),
    backButtonLabel: computed(() => {
      const activeStep = state.steps().find((step) => step.isActive);
      return activeStep?.backLabel || 'back';
    }),
    resetButtonLabel: computed(() => {
      const activeStep = state.steps().find((step) => step.isActive);
      return activeStep?.resetLabel || 'Reset';
    }),
    currentStepMinHeight: computed(() => {
      const activeStep = state.steps().find((step) => step.isActive);
      return activeStep?.minHeight;
    }),
  })),

  withMethods((store) => ({
    // Initialize the store with steps
    initialize(steps: WizardStep[]): void {
      if (!steps || steps.length === 0) return;

      patchState(store, {
        currentStep: steps[0].id,
        steps: steps.map((step, index) => ({
          ...step,
          isActive: index === 0,
          isCompleted: false,
        })),
      });
    },

    // Set the current step
    setStep(stepId: string): void {
      const steps = store.steps();
      const stepIndex = steps.findIndex((s) => s.id === stepId);

      if (stepIndex === -1) {
        console.error(`Step with id ${stepId} not found`);
        return;
      }

      patchState(store, {
        currentStep: stepId,
        steps: steps.map((step, index) => ({
          ...step,
          isActive: step.id === stepId,
          isCompleted: index < stepIndex || step.isCompleted,
        })),
      });
    },

    // Reset to initial state
    reset(): void {
      const initialSteps = store.steps().map((step, index) => ({
        ...step,
        isActive: index === 0,
        isCompleted: false,
      }));

      patchState(store, {
        currentStep: initialSteps.length > 0 ? initialSteps[0].id : '',
        steps: initialSteps,
      });
    },

    // Navigate to the next step
    goToNextStep(): boolean {
      const steps = store.steps();
      const currentStepIndex = steps.findIndex((step) => step.isActive);

      if (currentStepIndex === -1 || currentStepIndex >= steps.length - 1) {
        return false;
      }

      const nextStep = steps[currentStepIndex + 1];
      this.setStep(nextStep.id);
      return true;
    },

    // Navigate to the previous step
    goToPreviousStep(): boolean {
      const steps = store.steps();
      const currentStepIndex = steps.findIndex((step) => step.isActive);

      if (currentStepIndex <= 0) {
        return false;
      }

      const previousStep = steps[currentStepIndex - 1];
      this.setStep(previousStep.id);
      return true;
    },

    // Update the steps array
    updateSteps(steps: WizardStep[]): void {
      patchState(store, { steps });
    },

    // Mark all steps up to a specific step as completed
    markStepsAsCompleted(targetStepId: string): void {
      const steps = store.steps();
      const targetIndex = steps.findIndex((s) => s.id === targetStepId);

      if (targetIndex === -1) return;

      const updatedSteps = steps.map((step, index) => ({
        ...step,
        isCompleted: index <= targetIndex,
      }));

      patchState(store, { steps: updatedSteps });
    },

    // Check if can navigate to a specific step
    canNavigateToStep(stepId: string): boolean {
      const step = store.steps().find((s) => s.id === stepId);
      return step ? step.isCompleted : false;
    },
  })),
);
